import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../styles/quill-dark-theme.css';
import AudioClipDialog from './AudioClipDialog';
import AudioPlayer from './AudioPlayer';

import ImageResize from 'quill-resize-module';

// Fix link sanitization to ensure proper anchor tags are created  
const Link = Quill.import('formats/link');
Link.sanitize = function(url) {
  // Ensure URLs have proper protocol
  if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('mailto:')) {
    return `https://${url}`;
  }
  return url;
};

// Configure Quill to preserve image style attributes and dimensions
const Image = Quill.import('formats/image');
class CustomImage extends Image {
  static create(value) {
    const node = super.create(value);
    if (typeof value === 'object') {
      Object.keys(value).forEach(key => {
        node.setAttribute(key, value[key]);
      });
    }
    return node;
  }

  static formats(domNode) {
    const formats = {};
    if (domNode.hasAttribute('width')) {
      formats.width = domNode.getAttribute('width');
    }
    if (domNode.hasAttribute('height')) {
      formats.height = domNode.getAttribute('height');
    }
    if (domNode.style.width) {
      formats.width = domNode.style.width;
    }
    if (domNode.style.height) {
      formats.height = domNode.style.height;
    }
    return formats;
  }

  format(name, value) {
    if (name === 'width' || name === 'height') {
      if (value) {
        this.domNode.setAttribute(name, value);
        this.domNode.style[name] = value;
      } else {
        this.domNode.removeAttribute(name);
        this.domNode.style[name] = '';
      }
    } else {
      super.format(name, value);
    }
  }
}

CustomImage.blotName = 'image';
CustomImage.tagName = 'IMG';
Quill.register(CustomImage);

// Register the custom Link format
Quill.register(Link, true);

// Register resize module
Quill.register('modules/imageResize', ImageResize);

const ContentEditor = ({
  isOpen,
  onClose,
  onSave,
  contentType = 'news',
  editingContent = null,
  categories = [],
  showScheduling = true,
  showVisibility = true,
  showAudioSupport = false,
  musicxmlId = null,
  placeholder = 'Write your content...'
}) => {
  const { user } = useAuth();
  const { t } = useLanguage();

  // Convert a UTC timestamp to the local "YYYY-MM-DDTHH:mm" format that
  // datetime-local inputs expect
  const toLocalDateTimeInput = (utcTimestamp) => {
    const date = new Date(utcTimestamp);
    date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
    return date.toISOString().slice(0, 16);
  };

  const [formData, setFormData] = useState({
    title: '',
    contentText: '',
    contentHtml: '',
    status: 'draft',
    visibility: 'public',
    category: '',
    tags: [],
    publishAt: '',
    expireAt: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tagInput, setTagInput] = useState('');
  const [showAudioDialog, setShowAudioDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [audioClips, setAudioClips] = useState([]);
  
  const textareaRef = useRef(null);
  const quillRef = useRef(null);

  // Custom link handler to improve link creation
  const linkHandler = useCallback(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    
    const range = quill.getSelection();
    if (!range) return;
    
    const currentLink = quill.getFormat(range.index, range.length).link;
    const url = prompt('Enter URL:', currentLink || 'https://');
    
    if (url) {
      quill.format('link', url);
    }
  }, []);

  // Memoize Quill modules configuration
  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['link', 'image', 'video'],
        ['blockquote', 'code-block'],
        ['clean']
      ],
      handlers: {
        link: linkHandler
      }
    },
    imageResize: {}
  }), [linkHandler]);

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 'indent', 'align',
    'link', 'image', 'video', 'blockquote', 'code-block',
    'width', 'height', 'style' // Add support for image sizing
  ];

  // Reset form when modal opens/closes or editing content changes
  useEffect(() => {
    if (isOpen) {
      if (editingContent) {
        // console.log('ContentEditor: editingContent received:', editingContent);
        // console.log('ContentEditor: content_text:', editingContent.content_text);
        // console.log('ContentEditor: content_html:', editingContent.content_html);
        // If we have HTML content, use it; otherwise convert text to basic HTML
        let htmlContent = editingContent.content_html;
        if (!htmlContent && editingContent.content_text) {
          // Convert plain text to basic HTML (preserve line breaks)
          htmlContent = editingContent.content_text.replace(/\n/g, '<br>');
        }
        
        setFormData({
          title: editingContent.title || '',
          contentText: editingContent.content_text || '',
          contentHtml: htmlContent || '',
          status: editingContent.status || 'draft',
          visibility: editingContent.visibility || 'public',
          category: editingContent.category || '',
          tags: editingContent.tags || [],
          publishAt: editingContent.publish_at ? toLocalDateTimeInput(editingContent.publish_at) : '',
          expireAt: editingContent.expire_at ? toLocalDateTimeInput(editingContent.expire_at) : ''
        });
      } else {
        setFormData({
          title: '',
          contentText: '',
          contentHtml: '',
          status: 'draft',
          visibility: 'public',
          category: '',
          tags: [],
          publishAt: '',
          expireAt: ''
        });
      }
      setError(null);
      setTagInput('');
    }
  }, [isOpen, editingContent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const contentData = {
        ...formData,
        // datetime-local values are local time with no timezone; convert to
        // ISO UTC so the server stores the instant the user actually picked
        publishAt: formData.publishAt ? new Date(formData.publishAt).toISOString() : '',
        expireAt: formData.expireAt ? new Date(formData.expireAt).toISOString() : '',
        contentType,
        authorUserId: user.id,
        authorName: user.email,
        authorEmail: user.email,
        userRole: user.role,
        userId: user.id
      };

      if (editingContent) {
        contentData.contentId = editingContent.id;
      }

      await onSave(contentData);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save content');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && e.target.name === 'tagInput') {
      e.preventDefault();
      handleAddTag();
    }
  };

  // Load audio clips for preview when component opens
  useEffect(() => {
    if (isOpen && showAudioSupport) {
      loadAudioClips();
    }
  }, [isOpen, showAudioSupport]);

  const loadAudioClips = async () => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const params = new URLSearchParams({
        operation: 'getAudioClips',
        userId: user.id
      });
      
      if (musicxmlId) {
        params.append('musicxmlId', musicxmlId);
      }
      
      const response = await fetch(`${API_BASE}/api/audio-clips?${params}`);
      
      if (response.ok) {
        const clipsData = await response.json();
        setAudioClips(clipsData);
      }
    } catch (err) {
      console.error('Failed to load audio clips:', err);
    }
  };

  const handleAudioClipSelected = (clip) => {
    const audioMarkup = `[audio:${clip.name}]`;
    insertTextAtCursor(audioMarkup);
    setShowAudioDialog(false);
  };

  const handleQuillChange = (content, delta, source, editor) => {
    const htmlContent = content;
    const textContent = editor.getText();
    
    setFormData(prev => ({
      ...prev,
      contentText: textContent,
      contentHtml: htmlContent
    }));
  };

  const insertTextAtCursor = (text) => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection();
      if (range) {
        quill.insertText(range.index, text);
        quill.setSelection(range.index + text.length);
      } else {
        quill.insertText(quill.getLength() - 1, text);
      }
    } else {
      // Fallback for textarea mode
      const textarea = textareaRef.current;
      if (!textarea) return;
      
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentText = formData.contentText;
      
      const newText = currentText.substring(0, start) + text + currentText.substring(end);
      
      setFormData(prev => ({ ...prev, contentText: newText }));
      
      // Restore cursor position after the inserted text
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + text.length, start + text.length);
      }, 0);
    }
  };

  const parseContentWithAudio = (content) => {
    if (!content) return [];
    
    const parts = [];
    const audioRegex = /\[audio:([^\]]+)\]/g;
    let lastIndex = 0;
    let match;
    
    while ((match = audioRegex.exec(content)) !== null) {
      // Add text before the audio markup
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: content.substring(lastIndex, match.index)
        });
      }
      
      // Add audio component
      const clipName = match[1];
      const clip = audioClips.find(c => c.name === clipName);
      parts.push({
        type: 'audio',
        clipName: clipName,
        clip: clip
      });
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < content.length) {
      parts.push({
        type: 'text',
        content: content.substring(lastIndex)
      });
    }
    
    return parts;
  };

  // Function to render content with embedded audio players for preview
  const renderContentWithAudioForPreview = (content) => {
    if (!content) return null;
    
    const audioRegex = /\[audio:([^\]]+)\]/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    let key = 0;
    
    while ((match = audioRegex.exec(content)) !== null) {
      // Add content before the audio markup
      if (match.index > lastIndex) {
        const beforeText = content.substring(lastIndex, match.index);
        parts.push(
          <span 
            key={`text-${key++}`} 
            dangerouslySetInnerHTML={{ __html: beforeText }}
          />
        );
      }
      
      // Add audio player
      const clipName = match[1];
      const clip = audioClips.find(c => c.name === clipName);
      parts.push(
        <div 
          key={`audio-${key++}`} 
          style={{ margin: '12px 0' }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
        >
          {clip ? (
            <AudioPlayer
              clipName={clip.name}
              fileUrl={clip.file_url}
              compact={false}
              showTitle={true}
            />
          ) : (
            <div style={{
              padding: '8px 12px',
              background: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              color: '#dc2626',
              fontSize: '14px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              ⚠️ Audio clip "{clipName}" not found
            </div>
          )}
        </div>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining content
    if (lastIndex < content.length) {
      const remainingText = content.substring(lastIndex);
      parts.push(
        <span 
          key={`text-${key++}`} 
          dangerouslySetInnerHTML={{ __html: remainingText }}
        />
      );
    }
    
    // If no audio markup found, just render the content normally
    if (parts.length === 0) {
      return <div dangerouslySetInnerHTML={{ __html: content }} />;
    }
    
    return <div>{parts}</div>;
  };


  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div 
        className="content-editor-dialog"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#1f2937',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '800px',
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
        }}
      >
        {/* Header */}
        <div style={{
          background: '#065f46',
          color: '#ffffff',
          padding: '16px 24px',
          borderRadius: '12px 12px 0 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
            {editingContent ? `Edit ${contentType}` : `Create ${contentType}`}
          </h2>
          <button 
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#ffffff',
              fontSize: '24px',
              cursor: 'pointer',
              width: '32px',
              height: '32px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        {/* Scrollable Form Content */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '24px',
          background: '#1f2937'
        }}>
          <form id="content-editor-form" onSubmit={handleSubmit} style={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
          {error && (
            <div style={{
              background: '#991b1b',
              border: '1px solid #dc2626',
              color: '#fecaca',
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '16px'
            }}>
              {error}
            </div>
          )}

          {/* Title */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#e5e7eb',
              marginBottom: '6px'
            }}>
              Title {contentType === 'news' ? '*' : ''}
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              required={contentType === 'news'}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #4b5563',
                borderRadius: '6px',
                fontSize: '14px',
                background: '#374151',
                color: '#f9fafb'
              }}
              placeholder="Enter title..."
            />
          </div>

          {/* Content */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <label style={{
                fontSize: '14px',
                fontWeight: '500',
                color: contentType === 'tip' ? '#e5e7eb' : '#374151'
              }}>
                Content *
              </label>
              
              <div style={{ display: 'flex', gap: '8px' }}>
                {showAudioSupport && (
                  <button
                    type="button"
                    onClick={() => setShowAudioDialog(true)}
                    style={{
                      padding: '6px 12px',
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    🎵 {t('audioClips.insertAudio')}
                  </button>
                )}
                
                {showAudioSupport && (
                  <button
                    type="button"
                    onClick={() => setShowPreview(!showPreview)}
                    style={{
                      padding: '6px 12px',
                      background: showPreview ? '#10b981' : '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    {showPreview ? '📝 Edit' : '👁️ Preview'}
                  </button>
                )}
              </div>
            </div>
            
            {!showPreview ? (
              <div className="content-editor-quill">
                <ReactQuill
                  ref={quillRef}
                  value={formData.contentHtml}
                  onChange={handleQuillChange}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder={placeholder + (showAudioSupport ? ' Use [audio:clipName] to embed audio clips.' : '')}
                  style={{
                    height: '300px',
                    marginBottom: '42px'
                  }}
                />
              </div>
            ) : (
              <div style={{
                minHeight: '200px',
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                background: '#f9fafb',
                lineHeight: '1.5',
                fontFamily: 'inherit',
                color: '#374151'
              }}>
                {(formData.contentHtml || formData.contentText) ? (
                  renderContentWithAudioForPreview(formData.contentHtml || formData.contentText)
                ) : (
                  <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>
                    {placeholder}
                  </span>
                )}
              </div>
            )}
            
            {showAudioSupport && (
              <div style={{
                fontSize: '11px',
                color: '#6b7280',
                marginTop: '4px'
              }}>
                {t('audioClips.embedTip')}
              </div>
            )}
          </div>

          {/* Row 1: Status and Visibility */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            {/* Status */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #4b5563',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: '#374151',
                  color: '#f9fafb'
                }}
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            {/* Visibility */}
            {showVisibility && (
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  Visibility
                </label>
                <select
                  value={formData.visibility}
                  onChange={(e) => setFormData(prev => ({ ...prev, visibility: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #4b5563',
                    borderRadius: '6px',
                    fontSize: '14px',
                    background: '#374151',
                    color: '#f9fafb'
                  }}
                >
                  <option value="public">Public</option>
                  <option value="members_only">Members Only</option>
                  <option value="admin_only">Admin Only</option>
                </select>
              </div>
            )}
          </div>

          {/* Category */}
          {categories.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Category
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #4b5563',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: '#374151',
                  color: '#f9fafb'
                }}
              >
                <option value="">Select category...</option>
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Tags */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#e5e7eb',
              marginBottom: '6px'
            }}>
              Tags
            </label>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <input
                type="text"
                name="tagInput"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleKeyPress}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: '1px solid #4b5563',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: '#374151',
                  color: '#f9fafb'
                }}
                placeholder="Add a tag..."
              />
              <button
                type="button"
                onClick={handleAddTag}
                style={{
                  padding: '8px 16px',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Add
              </button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {formData.tags.map(tag => (
                <span
                  key={tag}
                  style={{
                    background: '#e5e7eb',
                    color: '#374151',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#6b7280',
                      cursor: 'pointer',
                      fontSize: '14px',
                      lineHeight: 1
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Scheduling */}
          {showScheduling && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  Publish At
                </label>
                <input
                  type="datetime-local"
                  value={formData.publishAt}
                  onChange={(e) => setFormData(prev => ({ ...prev, publishAt: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #4b5563',
                    borderRadius: '6px',
                    fontSize: '14px',
                    background: '#374151',
                    color: '#f9fafb'
                  }}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  Expire At
                </label>
                <input
                  type="datetime-local"
                  value={formData.expireAt}
                  onChange={(e) => setFormData(prev => ({ ...prev, expireAt: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #4b5563',
                    borderRadius: '6px',
                    fontSize: '14px',
                    background: '#374151',
                    color: '#f9fafb'
                  }}
                />
              </div>
            </div>
          )}
          </form>
        </div>

        {/* Fixed Actions Footer */}
        <div style={{
          borderTop: '1px solid #374151',
          padding: '16px 24px',
          background: '#1f2937',
          borderRadius: '0 0 12px 12px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px'
          }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '10px 20px',
                background: '#374151',
                color: '#d1d5db',
                border: '1px solid #4b5563',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#4b5563';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#374151';
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="content-editor-form"
              disabled={loading}
              style={{
                padding: '10px 20px',
                background: loading ? '#9ca3af' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.background = '#2563eb';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.background = '#3b82f6';
                }
              }}
            >
              {loading ? 'Saving...' : (editingContent ? 'Update' : 'Create')}
            </button>
          </div>
        </div>
      </div>
      
      {/* Audio Clip Dialog */}
      {showAudioSupport && (
        <AudioClipDialog
          isOpen={showAudioDialog}
          onClose={() => setShowAudioDialog(false)}
          onClipSelected={handleAudioClipSelected}
          musicxmlId={musicxmlId}
        />
      )}
    </div>
  );
};

export default ContentEditor;